import type { ConversionResult } from '@/types';

export class UnitConverter {
  // 长度转换常量
  private static readonly LENGTH_RATES = {
    cm: { in: 0.393701, ft: 0.0328084, m: 0.01, mm: 10 },
    in: { cm: 2.54, ft: 0.0833333, m: 0.0254, mm: 25.4 },
    ft: { cm: 30.48, in: 12, m: 0.3048, mm: 304.8 },
    m: { cm: 100, in: 39.3701, ft: 3.28084, mm: 1000 },
    mm: { cm: 0.1, in: 0.0393701, ft: 0.00328084, m: 0.001 }
  } as const;

  // 重量转换常量
  private static readonly WEIGHT_RATES = {
    g: { kg: 0.001, lb: 0.00220462, oz: 0.035274, mg: 1000 },
    kg: { g: 1000, lb: 2.20462, oz: 35.274, mg: 1000000 },
    lb: { g: 453.592, kg: 0.453592, oz: 16, mg: 453592 },
    oz: { g: 28.3495, kg: 0.0283495, lb: 0.0625, mg: 28349.5 },
    mg: { g: 0.001, kg: 0.000001, lb: 0.00000220462, oz: 0.000035274 }
  } as const;

  /**
   * 转换长度单位
   */
  static convertLength(value: number, from: string, to: string): ConversionResult {
    if (from === to) {
      return {
        from: { value, unit: from },
        to: { value, unit: to }
      };
    }

    const fromRates = this.LENGTH_RATES[from as keyof typeof this.LENGTH_RATES];
    const rate = fromRates?.[to as keyof typeof fromRates];

    if (rate === undefined) {
      throw new Error(`Unsupported conversion: ${from} to ${to}`);
    }

    const convertedValue = value * rate;

    return {
      from: { value, unit: from },
      to: { value: convertedValue, unit: to }
    };
  }

  /**
   * 转换重量单位
   */
  static convertWeight(value: number, from: string, to: string): ConversionResult {
    if (from === to) {
      return {
        from: { value, unit: from },
        to: { value, unit: to }
      };
    }

    const fromRates = this.WEIGHT_RATES[from as keyof typeof this.WEIGHT_RATES];
    const rate = fromRates?.[to as keyof typeof fromRates];

    if (rate === undefined) {
      throw new Error(`Unsupported conversion: ${from} to ${to}`);
    }

    const convertedValue = value * rate;

    return {
      from: { value, unit: from },
      to: { value: convertedValue, unit: to }
    };
  }

  /**
   * 批量转换长度到多个单位
   */
  static convertLengthToMultiple(value: number, from: string, targets: string[]): Record<string, number> {
    const results: Record<string, number> = {};

    targets.forEach(target => {
      const result = this.convertLength(value, from, target);
      results[target] = result.to.value;
    });

    return results;
  }

  /**
   * 批量转换重量到多个单位
   */
  static convertWeightToMultiple(value: number, from: string, targets: string[]): Record<string, number> {
    const results: Record<string, number> = {};

    targets.forEach(target => {
      const result = this.convertWeight(value, from, target);
      results[target] = result.to.value;
    });

    return results;
  }

  /**
   * 格式化数值显示
   */
  static formatValue(value: number, precision: number = 3): string {
    if (value === 0) return '0';

    // 对于非常小的数，使用更多小数位
    if (value < 0.001) {
      return value.toFixed(6).replace(/\.?0+$/, '');
    }

    // 对于整数，不显示小数位
    if (Number.isInteger(value)) {
      return value.toString();
    }

    return value.toFixed(precision).replace(/\.?0+$/, '');
  }

  /**
   * 验证输入值
   */
  static validateInput(value: string): boolean {
    const num = parseFloat(value);
    return !isNaN(num) && num >= 0;
  }

  /**
   * 获取所有支持的长度单位
   */
  static getSupportedLengthUnits(): string[] {
    return Object.keys(this.LENGTH_RATES);
  }

  /**
   * 获取所有支持的重量单位
   */
  static getSupportedWeightUnits(): string[] {
    return Object.keys(this.WEIGHT_RATES);
  }

  /**
   * 实时转换（用于输入框）
   */
  static createLengthConverter(fromUnit: string, toUnit: string) {
    return (inputValue: string): string => {
      if (!this.validateInput(inputValue)) return '';

      const value = parseFloat(inputValue);
      const result = this.convertLength(value, fromUnit, toUnit);
      return this.formatValue(result.to.value);
    };
  }

  /**
   * 实时重量转换
   */
  static createWeightConverter(fromUnit: string, toUnit: string) {
    return (inputValue: string): string => {
      if (!this.validateInput(inputValue)) return '';

      const value = parseFloat(inputValue);
      const result = this.convertWeight(value, fromUnit, toUnit);
      return this.formatValue(result.to.value);
    };
  }
}

// 常用转换函数导出
export const convertCmToInches = (cm: number): number => cm * 0.393701;
export const convertInchesToCm = (inches: number): number => inches / 0.393701;
export const convertGramsToLbs = (grams: number): number => grams * 0.00220462;
export const convertLbsToGrams = (lbs: number): number => lbs / 0.00220462;
export const convertGramsToOz = (grams: number): number => grams * 0.035274;
export const convertOzToGrams = (oz: number): number => oz / 0.035274;
export const convertLbsToOz = (lbs: number): number => lbs * 16;
export const convertOzToLbs = (oz: number): number => oz / 16;